<template>
  <div class="guides">
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("docs.guidesTitle") }}</h1>
        <p class="page-head__lede">{{ t("docs.guidesBody") }}</p>
      </div>
    </div>

    <div class="guides__layout">
      <nav class="guides__toc">
        <a v-for="g in guides" :key="g.id" :href="`#${g.id}`">{{ t(`guides.${g.id}.title`) }}</a>
      </nav>

      <div class="guides__body">
        <section v-for="g in guides" :key="g.id" :id="g.id" class="card">
          <h3>{{ t(`guides.${g.id}.title`) }}</h3>
          <p class="guides__summary">{{ t(`guides.${g.id}.summary`) }}</p>
          <div v-for="(block, i) in g.blocks" :key="i">
            <h4 v-if="block.heading">{{ block.heading }}</h4>
            <p v-if="block.text" class="guides__text">{{ block.text }}</p>
            <p v-if="block.link" class="guides__link">
              <a :href="block.link.href" target="_blank" rel="noopener">{{ block.link.label }} ↗</a>
            </p>
            <p v-if="block.see" class="guides__see">
              {{ t("docs.seeAlso") }}
              <a v-for="id in block.see" :key="id" :href="`#${id}`">{{ t(`guides.${id}.title`) }}</a>
            </p>
            <ol v-if="block.steps" class="guides__steps">
              <li v-for="(step, s) in block.steps" :key="s">{{ step }}</li>
            </ol>
            <template v-if="block.code">
              <div v-if="block.langs" class="guides__tabs">
                <button
                  v-for="lang in block.langs"
                  :key="lang"
                  type="button"
                  class="guides__tab"
                  :class="{ 'guides__tab--on': activeLang(i) === lang }"
                  @click="setLang(i, lang)"
                >
                  {{ lang }}
                </button>
              </div>
              <pre class="guides__code">{{ codeFor(block, i) }}</pre>
            </template>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { GUIDES } from "../lib/guides";

const { t } = useI18n();

/**
 * The hand-written half of the documentation.
 *
 * SEPARATE FROM THE REFERENCE ON PURPOSE. The reference is generated and cannot
 * drift; these are prose and can. Keeping them apart means nobody is tempted to
 * hand-edit the generated page, and the pages that DO need a human — how the
 * OAuth round trip actually goes, why a webhook signature fails, what to do when
 * a token is refused — are all in one file a reviewer can read end to end.
 *
 * The webhook verification snippets are the load-bearing content here: the most
 * common webhook support ticket in the industry is a developer verifying against
 * a re-serialised body, and every snippet below signs the raw bytes for that
 * reason.
 */

const guides = GUIDES;
const langs = ref({});

function activeLang(index) {
  return langs.value[index] || (guides.flatMap((g) => g.blocks)[index] || {}).langs?.[0];
}

function setLang(index, lang) {
  langs.value = { ...langs.value, [index]: lang };
}

function codeFor(block, index) {
  if (!block.langs) return block.code;
  const lang = langs.value[index] || block.langs[0];
  return block.code[lang];
}
</script>

<style scoped>
.guides__layout { display: flex; gap: 2rem; align-items: flex-start; }
.guides__toc {
  flex: 0 0 12rem;
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.9rem;
}
.guides__toc a { color: var(--ink-muted); text-decoration: none; }
.guides__toc a:hover { color: var(--accent); }
.guides__toc a {
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  border-left: 2px solid transparent;
  transition: color var(--transition), border-color var(--transition);
}
.guides__toc a:hover { border-left-color: var(--line-strong); text-decoration: none; }
.guides__body { flex: 1 1 auto; min-width: 0; }
.guides__body > section { scroll-margin-top: 72px; }
.guides__text { white-space: pre-wrap; }
.guides__summary { color: var(--ink-muted); margin-top: calc(-1 * var(--space-2)); }
.guides__link { margin-top: var(--space-2); font-weight: 600; }
.guides__see { font-size: 0.9rem; margin-top: var(--space-2); }
.guides__see a { margin-left: 0.35rem; }
/* A sub-heading that follows a paragraph needs room above it; base.css only
   gives headings a bottom margin, which is right in a card and wrong in a
   long-form document. */
/*
 * Each block renders as its own <div>, so every heading is the :first-child of
 * something — scoping the reset to the first BLOCK in the card is what actually
 * distinguishes "top of the section" from "a heading part-way down it".
 */
.guides__body h4 { margin-top: var(--space-5); }
.guides__body .card > div:first-of-type > h4 { margin-top: 0; }
.guides__steps { padding-left: 1.2rem; }
.guides__steps li { margin-bottom: 0.35rem; }
.guides__tabs { display: flex; gap: 0.3rem; margin-bottom: 0.3rem; }
.guides__tab {
  border: 1px solid var(--line);
  background: transparent;
  color: var(--ink-muted);
  border-radius: 4px;
  padding: 0.1rem 0.5rem;
  font-size: 0.8rem;
  cursor: pointer;
}
.guides__tab--on { background: var(--accent); border-color: var(--accent); color: #fff; }
.guides__code {
  background: var(--bg-code);
  padding: 0.7rem;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.82rem;
  line-height: 1.45;
}

@media (max-width: 820px) {
  .guides__layout { flex-direction: column; }
  .guides__toc { position: static; flex-direction: row; flex-wrap: wrap; gap: 0.75rem; }
}
</style>
